/**
 * Рублийн хэлбэлзэл — baseline график + үнийн тэнхлэг.
 *
 * ⚠️ ӨНГӨНИЙ УТГА: ханш ӨСӨХ нь импортлогчид МУУ мэдээ (гутал үнэтэй
 *    болно). Тиймээс өсөлт нь ягаан (alert), бууралт нь оюу (accent).
 *    Хөрөнгийн зах зээлийн зуршлаар «өсөлт = ногоон» гэж будвал
 *    захирал эсрэгээр нь уншина. Дэлгэрэнгүй `fx-plot.tsx`.
 *
 * МУЖ СОЛИХ
 *  14 хоног нь дашбоардын хариунд аль хэдийн ирдэг тул дахин
 *  татахгүй. Бусад муж `/ref/fx`-ээс — тэр route 400 мөр хүртэл
 *  буцаадаг тул 1 жил (366) багтана.
 *
 * ⚠️ ХҮРТЭЭМЖ: 365 мөрийн нуувч хүснэгт гаргахын оронд SVG-ийн
 *    `aria-label` муруйн ГОЛ баримтуудыг өгүүлнэ (эхлэл, төгсгөл,
 *    дээд, доод). Толгойн тоо, доорх статистик нь жинхэнэ текст тул
 *    tooltip нь ямар ч утгын ЦОРЫН ГАНЦ зам биш.
 *
 * ⚠️ Муж нь дотоод `useState`-д — URL-д ХАДГАЛАХГҮЙ. `?range=` нь
 *    Орлогын графикийнх; хоёр блок нэг параметр хуваалцвал нэгийг нь
 *    сольсон хүн нөгөөг нь санамсаргүй өөрчилнө.
 */

import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { formatRate } from '@boots/domain';
import { api, type Envelope } from '../../lib/api';
import type { Dashboard } from '../../lib/types';
import { Section } from '../section';
import { EmptyState } from '../empty-state';
import { Tabs } from '../ui';
import { CountUp } from '../count-up';
import { FxChart, Move, type FxRow } from './fx-chart';

type RangeKey = '14' | '30' | '90' | '365';

/** `since` — толгойн «…өмнө» шошго, табтай ижил хугацааг нэрлэнэ. */
const RANGES: { key: RangeKey; label: string; since: string }[] = [
  { key: '14', label: '14 хоног', since: '14 хоногийн өмнө' },
  { key: '30', label: '30 хоног', since: '30 хоногийн өмнө' },
  { key: '90', label: '90 хоног', since: '90 хоногийн өмнө' },
  { key: '365', label: '1 жил', since: '1 жилийн өмнө' },
];

/** Дашбоардын хариунд аль хэдийн байдаг муж — дахин татахгүй. */
const DEFAULT_RANGE: RangeKey = '14';

export function FxCard({ fx, showValue, compact = false }: {
  fx: Dashboard['fx'];
  showValue: boolean;
  /**
   * Нарийн баганад багтаах хэлбэр (дашбоард).
   *
   * ⚠️ Ханш нь дашбоардын ГОЛ мэдээлэл БИШ — өртөгт автоматаар
   *    ордоггүй лавлагаа (дүрэм 4). Өмнө нь бүтэн өргөнөөр, 190px
   *    графиктай зогсож, хуудсыг уртасгаж байв. Энд график намхан,
   *    мужийн таб нуугдана (14 хоног тогтмол).
   */
  compact?: boolean;
}) {
  const [range, setRange] = useState<RangeKey>(DEFAULT_RANGE);
  const [hover, setHover] = useState<number | null>(null);
  const days = Number(range);

  const history = useQuery({
    queryKey: ['fx-history', fx.currency, days],
    queryFn: async () => {
      const from = new Date();
      from.setUTCDate(from.getUTCDate() - days);
      const res = await api<Envelope<FxRow[]>>(
        `/ref/fx?currency=${fx.currency}&from=${encodeURIComponent(from.toISOString())}`,
      );
      // Route нь шинэ→хуучин эрэмбээр буцаадаг, график эсрэгээр зурна
      return [...res.data].reverse();
    },
    enabled: range !== DEFAULT_RANGE,
    staleTime: 5 * 60_000,
    // Муж солиход хуучин зургаа барина — skeleton анивчихгүй
    placeholderData: keepPreviousData,
  });

  const rows: FxRow[] = range === DEFAULT_RANGE ? fx.history : (history.data ?? []);
  const values = rows.map((r) => Number(r.rate));

  const rate = fx.rate === null ? null : Number(fx.rate);
  const prev = fx.prev === null ? null : Number(fx.prev);

  const changeRange = (r: RangeKey) => {
    setRange(r);
    setHover(null);
  };

  return (
    <Section
      feature={compact}
      title={compact ? 'Рублийн ханш' : 'Рублийн хэлбэлзэл'}
      subtitle="Монголбанк · албан ханш"
      actions={compact ? undefined : <Tabs tabs={RANGES} value={range} onChange={changeRange} />}
    >
      {rate === null ? (
        <EmptyState
          compact
          title="Ханшийн өгөгдөл алга"
          description="Монголбанкны ханш татагдаагүй байна. Худалдан авалтын нийт өртөг нь хадгалагдсан ханшаар тооцогдоно — энэ хэсэг зөвхөн харуулах зориулалттай."
        />
      ) : (
        <div className={`flex flex-col items-stretch gap-4 ${compact ? '' : 'lg:flex-row lg:gap-6'}`}>
          <FxHeadline
            currency={fx.currency}
            rate={rate}
            prev={prev}
            avg7={fx.avg7}
            values={values}
            since={RANGES.find((r) => r.key === range)!.since}
            compact={compact}
          />

          <div
            className={`flex min-w-0 flex-1 flex-col gap-1.5 transition-opacity ${
              history.isFetching ? 'opacity-60' : ''
            }`}
          >
            {values.length < 2 ? (
              <div className="h-[190px] animate-pulse rounded-well bg-well" />
            ) : (
              <FxChart
                rows={rows}
                values={values}
                days={days}
                hover={hover}
                onHover={setHover}
                showValue={showValue}
              />
            )}
          </div>
        </div>
      )}
    </Section>
  );
}

/** Толгой — өнөөдрийн ханш, өдрийн өөрчлөлт, мужийн өөрчлөлт. */
function FxHeadline({
  currency, rate, prev, avg7, values, since, compact,
}: {
  currency: string;
  rate: number;
  prev: number | null;
  avg7: string | null;
  values: number[];
  since: string;
  /** Нарийн баганад мужийн нийт өөрчлөлтийн блок гарахгүй. */
  compact: boolean;
}) {
  const diff = prev === null ? null : rate - prev;
  const open = values[0];
  const last = values.at(-1);

  return (
    <div className={`flex shrink-0 flex-col gap-1 ${compact ? '' : 'lg:w-[212px]'}`}>
      <span className="text-meta text-ink-2">1 {currency} = ₮</span>

      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1">
        {compact ? (
          // Дашбоард (dark glass) — Unbounded, өчигдрийн ханшаас гүйнэ
          <span className="font-display text-num-xl font-semibold">
            <CountUp text={formatRate(rate)} />
          </span>
        ) : (
          <span className="num text-metric-lg">{formatRate(rate)}</span>
        )}
        {diff === null ? null : compact ? (
          <span className={`pop-in rounded-xl px-2.5 py-1 ${diff >= 0 ? 'bg-alert-bg' : 'bg-accent-100'}`}>
            <Move value={diff} pct={prev ? (diff / prev) * 100 : null} />
          </span>
        ) : (
          <Move value={diff} pct={prev ? (diff / prev) * 100 : null} />
        )}
      </div>

      <span className="text-label text-ink-2">
        Өчигдөр {formatRate(prev)} · 7 хоногийн дундаж {formatRate(avg7)}
      </span>

      {/* Мужийн нийт өөрчлөлт — графикийн лавлах шугам яг энэ утга дээр */}
      {compact || open === undefined || last === undefined ? null : (
        <span className="mt-2 flex flex-col gap-0.5 border-t border-hair pt-2.5 text-label text-ink-2">
          {since}
          <span className="flex items-baseline gap-1.5">
            <span className="num text-h3 text-ink">{formatRate(open)}₮</span>
            <Move value={last - open} pct={open ? ((last - open) / open) * 100 : null} />
          </span>
        </span>
      )}
    </div>
  );
}
